import { BUTLER_TAB, type ButlerTabType } from '@cart-services/constants/butler-tab'
import { Platform, type PlatformType } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bButlerItemsService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-butler-items-service'
import { useFpB2bButlerService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-butler-service'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { Button } from '@pets-ui/Button/Button'
import { Container } from '@pets-ui/Container/Container'
import { Divider } from '@pets-ui/Divider/Divider'
import { Footer } from '@pets-ui/Footer/Footer'
import { FooterStack } from '@pets-ui/FooterStack/FooterStack'
import { Paper } from '@pets-ui/Paper/Paper'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Tab } from '@pets-ui/Tab/Tab'
import { TabContext } from '@pets-ui/TabContext/TabContext'
import { TabList } from '@pets-ui/TabList/TabList'
import { TabPanel } from '@pets-ui/TabPanel/TabPanel'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { AxiosError } from 'axios'
import { type ReactNode } from 'react'
import AiIcon from '../../assets/icons/ai-icon.svg?react'
import { butlerTabIconMap, butlerTabLabelMap } from '../../constants'
import { canSubmitAiRecognition } from '../../utils/can-submit-ai-recognition'
import { AiCsvUploadPanel } from '../AiCsvUploadPanel/AiCsvUploadPanel'
import { AiFileUploadPanel } from '../AiFileUploadPanel/AiFileUploadPanel'
import { useButler } from '../ButlerProvider/useButler'
import { useDropZone } from '../DropZone/useDropZone'

const styles = stylex.create({
  gradientText: {
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    backgroundImage: 'linear-gradient(165deg, #AFD8FF 16.07%, #06F 39.65%, #CA73FF 83.43%)',
  },
  footerStack: {
    padding: 0,
  },
  tabText: {
    marginLeft: '8px',
  },
})

const tabsMap: Record<PlatformType, ButlerTabType[]> = {
  [Platform.MERCHANT]: [BUTLER_TAB.FILE],
  [Platform.STM]: [BUTLER_TAB.CSV, BUTLER_TAB.FILE],
}

const submitLabelMap: Record<ButlerTabType, string> = {
  [BUTLER_TAB.FILE]: 'AI 辨識',
  [BUTLER_TAB.CSV]: '驗證檔案',
  [BUTLER_TAB.PHOTO]: 'AI 辨識',
}

export interface AiInputProcessorProps {
  brandSelector?: ReactNode
  onCsvSubmit?: (file: File) => void
  isCsvSubmitting?: boolean
}

export const AiInputProcessor = ({ brandSelector, onCsvSubmit, isCsvSubmitting = false }: AiInputProcessorProps) => {
  const isMobile = useResponsive('mobile')
  const { campaignId, campaignCode, platform, selectedTab, onSelectedTabChange } = useButler()
  const { createOrderFromPdf, isCreateOrderFromPdfLoading, setOrderId, setButlerErrors } = useFpB2bButlerService(campaignId, platform)
  const { deleteAllButlerItems, setFileName } = useFpB2bButlerItemsService(campaignId, platform)
  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaignCode })
  const customerId = userInfo?.platform_uid
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const pdfDropZone = useDropZone()
  const csvDropZone = useDropZone()
  const showBrandSelector = brandSelector && selectedTab === BUTLER_TAB.FILE
  const csvFile = csvDropZone.files[0]
  const pdfFile = pdfDropZone.files[0]
  const canSubmit = canSubmitAiRecognition({ selectedTab, csvFile, pdfFile, customerId })

  const handleCreateOrderFromPdf = async () => {
    try {
      if (!customerId) {
        throw new Error('User id not found')
      }
      if (!pdfFile) {
        throw new Error('No file uploaded')
      }
      const orderId = await createOrderFromPdf({
        file: pdfFile,
        customerId: Number(customerId),
      })
      setOrderId(orderId)
    }
    catch (error) {
      if (error instanceof AxiosError) {
        setButlerErrors({
          createOrder: error.response?.data.message || '轉換失敗',
        })
        if (error.status === 422) {
          openSnackbar({
            message: '檔案過大，請上傳小於 10MB 的檔案',
            severity: 'error',
          })
        }
      }
      else {
        setButlerErrors({
          createOrder: error instanceof Error ? error.message : '轉換失敗',
        })
      }
    }
  }

  const handleClickAiRecognition = () => {
    if (selectedTab === BUTLER_TAB.CSV) {
      if (csvFile) onCsvSubmit?.(csvFile)
      return
    }
    deleteAllButlerItems()
    if (pdfFile) {
      setFileName(pdfFile.name)
    }
    if (selectedTab === BUTLER_TAB.FILE) {
      handleCreateOrderFromPdf()
    }
  }

  return (
    <>
      <Container pt={isMobile ? '16px' : '24px'} pb={isMobile ? '76px' : '84px'} display="flex" flexDirection="column" gap="24px" height="calc(100vh - 56px)">
        <VStack gap={2} alignItems="center" py={3}>
          <Text variant="h1" rootStyle={styles.gradientText} align="center">
            {isMobile
              ? (
                <>
                  歡迎使用
                  <br />
                  Shepherdtech 快速下單
                </>
              )
              : '歡迎使用 Shepherdtech 快速下單'}
          </Text>
          <Text variant="body" color={colors.grey600}>
            AI 極速產生採購單，工作效率提昇 30%
          </Text>
        </VStack>
        <Paper p="16px" mx={isMobile ? 4 : 0} elevation={0} flex={1} overflowY="hidden">
          <VStack height="100%">
            <TabContext value={selectedTab}>
              <TabList color="blue">
                {tabsMap[platform].map((tab) => {
                  const TabIcon = butlerTabIconMap[tab]
                  return (
                    <Tab key={tab} value={tab} onClick={() => onSelectedTabChange(tab)}>
                      {TabIcon && <TabIcon />}
                      <Text variant="subtitle" rootStyle={styles.tabText}>{butlerTabLabelMap[tab]}</Text>
                    </Tab>
                  )
                })}
              </TabList>
              <Divider />
              {showBrandSelector && brandSelector}
              <TabPanel value={BUTLER_TAB.CSV}>
                <AiCsvUploadPanel files={csvDropZone.files} handleDrop={csvDropZone.handleDrop} handleRemovePendingFile={csvDropZone.handleRemovePendingFile} />
              </TabPanel>
              <TabPanel value={BUTLER_TAB.FILE}>
                <AiFileUploadPanel files={pdfDropZone.files} handleDrop={pdfDropZone.handleDrop} handleRemovePendingFile={pdfDropZone.handleRemovePendingFile} />
              </TabPanel>
            </TabContext>
          </VStack>
        </Paper>
      </Container>
      <FooterStack enableContainer rootStyle={styles.footerStack}>
        <Footer ContainerProps={{ justifyContent: 'flex-end' }}>
          <Button
            startIcon={<AiIcon />}
            color="blue"
            variant="filled"
            width={isMobile ? undefined : '140px'}
            fullWidth={isMobile}
            onClick={handleClickAiRecognition}
            loading={isCreateOrderFromPdfLoading || isCsvSubmitting}
            disabled={!canSubmit}
          >
            {submitLabelMap[selectedTab]}
          </Button>
        </Footer>
      </FooterStack>
    </>
  )
}
